'use client';

import { Button, Icon, TextPassage } from '@petsmart-ui/sparky';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useDecisions } from '@libs/common-next';

import BirthdayOutlineIcon from './birthday-outline.icon.svg';
import { setProfileCompleteFlowOrigin } from './celebration';
import { hasInProgressCompleteProfileFlow } from './complete-profile-flow-session';
import type { ProfileCompletionReturnTo } from './constants';
import { COMPLETE_PROFILE_ROUTE, PROFILE_COMPLETION_STEP_LABELS } from './constants';
import { stageStartProfileStepperEvent } from './start-profile-stepper-event';
import { useCompleteProfileCelebration } from './use-complete-profile-celebration';
import { useProfileCompletionSteps } from './use-profile-completion-steps';

import './CompleteProfileBanner.scss';

type CompleteProfileBannerProps = Readonly<{
    returnTo?: ProfileCompletionReturnTo;
}>;

const STEP_ICONS = {
    pet: 'pet-outline',
    address: 'address-outline',
    notifications: 'feedback-outline',
} as const;

export function CompleteProfileBanner({ returnTo = 'about-you' }: CompleteProfileBannerProps) {
    const isFeatureEnabled = useDecisions('ff_complete_profile');
    const router = useRouter();

    const { steps, completedCount, totalCount, firstIncompleteStep, isLoading, error, isProfileComplete } =
        useProfileCompletionSteps({
            enabled: isFeatureEnabled,
        });

    const allStepsComplete = !isLoading && !error && completedCount === totalCount && totalCount > 0;

    const { isSuccess, isDismissed, isAlreadyCompleteOnLoad } = useCompleteProfileCelebration({
        enabled: isFeatureEnabled,
        isProfileComplete,
        allStepsComplete,
        completedCount,
        totalCount,
        isLoading,
        error,
        returnTo,
    });
    const [isNavigating, setIsNavigating] = useState(false);

    if (!isFeatureEnabled || error) {
        return null;
    }

    if (isLoading && !isSuccess) {
        return null;
    }

    if (isDismissed || (!isSuccess && isAlreadyCompleteOnLoad)) {
        return null;
    }

    if (isProfileComplete && !isSuccess && !hasInProgressCompleteProfileFlow()) {
        return null;
    }

    const displayCompleted = isSuccess ? totalCount : completedCount;
    let progressStateClass = '';
    if (isSuccess) {
        progressStateClass = 'complete-profile-banner__progress--success';
    } else if (displayCompleted > 0) {
        progressStateClass = 'complete-profile-banner__progress--active';
    }

    const handleCompleteClick = () => {
        setIsNavigating(true);
        setProfileCompleteFlowOrigin(returnTo);

        if (firstIncompleteStep) {
            stageStartProfileStepperEvent(returnTo, firstIncompleteStep);
        }

        router.push(COMPLETE_PROFILE_ROUTE);
    };

    return (
        <section
            className={`complete-profile-banner${isSuccess ? ' complete-profile-banner--success' : ''}`}
            data-testid="complete-profile-banner"
            aria-label={isSuccess ? 'Profile complete' : 'Complete your profile'}
        >
            <div className="complete-profile-banner__content">
                <div className="complete-profile-banner__header">
                    {isSuccess ? (
                        <BirthdayOutlineIcon
                            className="complete-profile-banner__title-icon"
                            data-testid="complete-profile-success-icon"
                            aria-hidden
                        />
                    ) : (
                        <Icon name="account-circle-outline" size="md" className="complete-profile-banner__title-icon" />
                    )}
                    <TextPassage className="complete-profile-banner__title" capLinelength={false}>
                        {isSuccess ? 'Profile complete!' : 'Complete your profile!'}
                    </TextPassage>
                    <TextPassage className="complete-profile-banner__subtitle">
                        {isSuccess
                            ? 'Your 1,000 rewards points will be reflected in your balance within 24 hours.'
                            : 'Earn 1,000 points by adding a pet, address, and opting-in to notifications'}
                    </TextPassage>
                </div>

                <div className="complete-profile-banner__progress-row">
                    <progress
                        className={`complete-profile-banner__progress ${progressStateClass}`}
                        value={displayCompleted}
                        max={totalCount}
                        data-testid="complete-profile-progress"
                    />
                    <TextPassage className="complete-profile-banner__counter" data-testid="complete-profile-counter">
                        {displayCompleted}/{totalCount}
                    </TextPassage>
                </div>

                <ul className="complete-profile-banner__steps">
                    {steps.map(step => {
                        const isStepComplete = isSuccess || step.isComplete;
                        return (
                            <li key={step.id} className="complete-profile-banner__step">
                                <Icon
                                    name={isStepComplete ? 'check-circle-fill' : STEP_ICONS[step.id]}
                                    size="sm"
                                    className={
                                        isStepComplete
                                            ? 'complete-profile-banner__step-icon--complete'
                                            : 'complete-profile-banner__step-icon'
                                    }
                                    data-testid={`complete-profile-step-icon-${step.id}`}
                                />
                                <TextPassage className="complete-profile-banner__step-label">
                                    {PROFILE_COMPLETION_STEP_LABELS[step.id]}
                                </TextPassage>
                            </li>
                        );
                    })}
                </ul>

                {!isSuccess && !allStepsComplete && (
                    <Button
                        className="complete-profile-banner__cta"
                        text="Complete"
                        size="lg"
                        variant="table-header"
                        fullWidth
                        loading={isNavigating}
                        disabled={isNavigating}
                        onClick={handleCompleteClick}
                        data-testid="complete-profile-cta"
                    />
                )}
            </div>
        </section>
    );
}
